// client/src/components/tendermaker/tendermakerApi.js
import { getAuth, onAuthStateChanged } from "firebase/auth";

// If React is served from tendermanagers.com, keep API_BASE empty and call /api/...
const API_BASE = process.env.REACT_APP_API_URL || "";

// Wait until Firebase finishes restoring auth state after refresh
function waitForUser(timeoutMs = 8000) {
  const auth = getAuth();
  if (auth.currentUser) return Promise.resolve(auth.currentUser);

  return new Promise((resolve, reject) => {
    const t = setTimeout(() => {
      unsub();
      reject(new Error("Not logged in (auth not ready)"));
    }, timeoutMs);

    const unsub = onAuthStateChanged(auth, (user) => {
      clearTimeout(t);
      unsub();
      if (!user) reject(new Error("Not logged in"));
      else resolve(user);
    });
  });
}

async function authHeaders() {
  const user = await waitForUser();
  const token = await user.getIdToken(); // keep false; only force refresh if needed
  return {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
}

async function reqJson(path, options = {}) {
  const r = await fetch(`${API_BASE}${path}`, options);
  const text = await r.text();
  if (!r.ok) {
    // Try to show backend json error cleanly
    try {
      const j = JSON.parse(text);
      throw new Error(j.error || j.detail || text || `HTTP ${r.status}`);
    } catch {
      throw new Error(text || `HTTP ${r.status}`);
    }
  }
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

/** --------- Existing Vault (legacy) ---------- **/
export async function tmPing() {
  return reqJson(`/api/tm/ping`);
}

export async function tmInitUpload({ fileName, contentType, kind }) {
  const headers = await authHeaders();
  return reqJson(`/api/tm/vault/init-upload`, {
    method: "POST",
    headers,
    body: JSON.stringify({ fileName, contentType, kind }),
  });
}

export async function tmCommitUpload({ key, fileName, contentType, kind }) {
  const headers = await authHeaders();
  return reqJson(`/api/tm/vault/commit`, {
    method: "POST",
    headers,
    body: JSON.stringify({ key, fileName, contentType, kind }),
  });
}

export async function tmListVault() {
  const headers = await authHeaders();
  return reqJson(`/api/tm/vault/list`, { headers });
}

export async function tmAnalyze(docId, mode = "auto") {
  const headers = await authHeaders();
  return reqJson(`/api/tm/analyze/${docId}`, {
    method: "POST",
    headers,
    body: JSON.stringify({ mode }),
  });
}

/** --------- Bid Workspace (multi-doc per bid) ---------- **/
export async function tmBidCreate(title) {
  const headers = await authHeaders();
  return reqJson(`/api/tm/bids/create`, {
    method: "POST",
    headers,
    body: JSON.stringify({ title }),
  });
}

export async function tmBidList() {
  const headers = await authHeaders();
  return reqJson(`/api/tm/bids/list`, { headers });
}

export async function tmBidDocs(bidId) {
  const headers = await authHeaders();
  return reqJson(`/api/tm/bids/${bidId}/docs`, { headers });
}

export async function tmBidInitUpload({ bidId, fileName, contentType }) {
  const headers = await authHeaders();
  return reqJson(`/api/tm/bids/${bidId}/init-upload`, {
    method: "POST",
    headers,
    body: JSON.stringify({ fileName, contentType }),
  });
}

export async function tmBidCommit({ bidId, key, fileName, contentType }) {
  const headers = await authHeaders();
  return reqJson(`/api/tm/bids/${bidId}/commit`, {
    method: "POST",
    headers,
    body: JSON.stringify({ key, fileName, contentType }),
  });
}

export async function tmBidAnalyze({ bidId, mode }) {
  const headers = await authHeaders();
  return reqJson(`/api/tm/bids/${bidId}/analyze`, {
    method: "POST",
    headers,
    body: JSON.stringify({ mode: mode || "auto" }),
  });
}

/** --------- Profile Vault ---------- **/
export async function tmProfileList() {
  const headers = await authHeaders();
  return reqJson(`/api/tm/profile/list`, { headers });
}

export async function tmProfileInitUpload({ fileName, contentType, masterId, label }) {
  const headers = await authHeaders();
  return reqJson(`/api/tm/profile/init-upload`, {
    method: "POST",
    headers,
    body: JSON.stringify({ fileName, contentType, masterId, label }),
  });
}

export async function tmProfileCommit({ key, fileName, contentType, masterId, label }) {
  const headers = await authHeaders();
  return reqJson(`/api/tm/profile/commit`, {
    method: "POST",
    headers,
    body: JSON.stringify({ key, fileName, contentType, masterId, label }),
  });
}

/** --------- Runs + Review ---------- **/
export async function tmGetRun(runId) {
  const headers = await authHeaders();
  return reqJson(`/api/tm/runs/${runId}`, { headers });
}

export async function tmSubmitReview(payload) {
  const headers = await authHeaders();
  return reqJson(`/api/tm/review`, {
    method: "POST",
    headers,
    body: JSON.stringify(payload),
  });
}
export async function tmListRuns({ bidId, status, limit, cursor } = {}) {
  const headers = await authHeaders();
  const qs = new URLSearchParams();
  if (bidId) qs.set("bidId", bidId);
  if (status) qs.set("status", status);
  if (limit) qs.set("limit", String(limit));
  if (cursor) qs.set("cursor", cursor);

  const q = qs.toString() ? `?${qs.toString()}` : "";
  // ✅ IMPORTANT: no "/list"
  return reqJson(`/api/tm/runs${q}`, { headers });
}

// === Profile Meta ===
export async function tmProfileMetaGet() {
  const headers = await authHeaders();
  return reqJson(`/api/tm/profile/meta`, { headers });
}

export async function tmProfileMetaSave(payload) {
  const headers = await authHeaders();
  return reqJson(`/api/tm/profile/meta`, {
    method: "POST",
    headers,
    body: JSON.stringify(payload || {}),
  });
}

// === Profile download ===
export async function tmProfileDownload(docId) {
  const headers = await authHeaders();
  return reqJson(`/api/tm/profile/download/${docId}`, { headers });
}

// === Run items ===
export async function tmRunItemsGet(runId) {
  const headers = await authHeaders();
  return reqJson(`/api/tm/runs/${runId}/items`, { headers });
}

export async function tmRunItemsBulkUpsert(runId, items) {
  const headers = await authHeaders();
  return reqJson(`/api/tm/runs/${runId}/items/bulk`, {
    method: "POST",
    headers,
    body: JSON.stringify({ items: items || [] }),
  });
}

export async function tmRunItemPatch(runId, itemId, patch) {
  const headers = await authHeaders();
  return reqJson(`/api/tm/runs/${runId}/items/${itemId}`, {
    method: "POST",
    headers,
    body: JSON.stringify(patch || {}),
  });
}

// === Drafts ===
export async function tmDraftSave(payload) {
  const headers = await authHeaders();
  return reqJson(`/api/tm/drafts/save`, {
    method: "POST",
    headers,
    body: JSON.stringify(payload || {}),
  });
}

export async function tmDraftGet(draftId) {
  const headers = await authHeaders();
  return reqJson(`/api/tm/drafts/${draftId}`, { headers });
}

export async function tmDraftAi(payload) {
  const headers = await authHeaders();
  return reqJson(`/api/tm/drafts/ai`, {
    method: "POST",
    headers,
    body: JSON.stringify(payload || {}),
  });
}

// === PDFs ===
export async function tmDocsMerge(payload) {
  const headers = await authHeaders();
  return reqJson(`/api/tm/docs/merge`, {
    method: "POST",
    headers,
    body: JSON.stringify(payload || {}),
  });
}

export async function tmDocsRenderPdf({ runId, itemId, title, html }) {
  const headers = await authHeaders();
  return reqJson(`/api/tm/docs/render-pdf`, {
    method: "POST",
    headers,
    body: JSON.stringify({ runId, itemId, title, html }),
  });
}

export async function tmAddTextBoxes({ key, runId, textBoxes }) {
  const headers = await authHeaders();
  return reqJson(`/api/tm/docs/add-text-boxes`, {
    method: "POST",
    headers,
    body: JSON.stringify({ key, runId, textBoxes }),
  });
}

export async function tmDocsExtractPages({ key, runId, pages }) {
  const headers = await authHeaders();
  return reqJson(`/api/tm/docs/extract-pages`, {
    method: "POST",
    headers,
    body: JSON.stringify({ key, runId, pages }),
  });
}

export async function tmDocsOverlay({ inputKeys, runId, signatureKey, stampKey, headerKey, footerKey, cfg }) {
  const headers = await authHeaders();
  return reqJson(`/api/tm/docs/overlay`, {
    method: "POST",
    headers,
    body: JSON.stringify({
      inputKeys,
      runId,
      signatureKey: signatureKey || null,
      stampKey: stampKey || null,
      headerKey: headerKey || null,
      footerKey: footerKey || null,
      cfg: cfg || null,
    }),
  });
}


// Download a bid doc (get signed URL)
export async function tmBidDocDownload(bidId, docDocId) {
  const headers = await authHeaders();
  return reqJson(`/api/tm/bids/${bidId}/docs/${docDocId}/download`, { headers });
}
// Promote a bid doc into Profile Vault (copy)
// payload: { bidId, docId, masterId?, label? }
export async function tmBidDocPromoteToProfile({ bidId, docId, masterId, label }) {
  const headers = await authHeaders();
  return reqJson(`/api/tm/bids/${bidId}/docs/${docId}/promote-to-profile`, {
    method: "POST",
    headers,
    body: JSON.stringify({ masterId, label }),
  });
}
// =====================
// Run item uploads (per requirement)
// =====================
export async function tmRunItemInitUpload({ runId, itemId, fileName, contentType }) {
  const headers = await authHeaders();
  return reqJson(`/api/tm/runs/${runId}/items/${itemId}/init-upload`, {
    method: "POST",
    headers,
    body: JSON.stringify({ fileName, contentType }),
  });
}

export async function tmRunItemCommitUpload({ runId, itemId, key, fileName, contentType }) {
  const headers = await authHeaders();
  return reqJson(`/api/tm/runs/${runId}/items/${itemId}/commit-upload`, {
    method: "POST",
    headers,
    body: JSON.stringify({ key, fileName, contentType }),
  });
}

export async function tmRunItemDownload({ runId, itemId }) {
  const headers = await authHeaders();
  return reqJson(`/api/tm/runs/${runId}/items/${itemId}/download`, { headers });
}

export async function tmRunItemPromoteToProfile({ runId, itemId, masterId, label }) {
  const headers = await authHeaders();
  return reqJson(`/api/tm/runs/${runId}/items/${itemId}/promote-to-profile`, {
    method: "POST",
    headers,
    body: JSON.stringify({ masterId, label }),
  });
}

export async function tmBidDocExtracted(bidId, docId) {
  const headers = await authHeaders();
  return reqJson(`/api/tm/bids/${bidId}/docs/${docId}/extracted`, { headers });
}

export async function tmBidDocToDocx(bidId, docId) {
  const headers = await authHeaders();
  return reqJson(`/api/tm/bids/${bidId}/docs/${docId}/to-docx`, {
    method: "POST",
    headers,
  });
}

export async function tmFileDownloadUrl(key) {
  const headers = await authHeaders();
  return reqJson(`/api/tm/file/download-url`, {
    method: "POST",
    headers,
    body: JSON.stringify({ key }),
  });
}

export function tmProxyPdfUrl(signedUrl) {
  const u = encodeURIComponent(String(signedUrl || ""));
  return `${API_BASE}/api/tm/proxy/pdf?url=${u}`;
}

